import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, money, formatApiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { CreditCard, Phone, Building2, Truck, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const PAYMENT_METHODS = [
    { id: "stripe", label: "Card (Visa/Mastercard)", icon: CreditCard, desc: "Secure card checkout via Stripe" },
    { id: "mtn_momo", label: "MTN Mobile Money", icon: Phone, desc: "Pay using your MTN MoMo account" },
    { id: "airtel_money", label: "Airtel Money", icon: Phone, desc: "Pay using your Airtel Money wallet" },
    { id: "bank_transfer", label: "Bank Transfer", icon: Building2, desc: "Transfer to our bank account" },
    { id: "cod", label: "Cash on Delivery", icon: Truck, desc: "Pay when your order arrives" },
];

export default function Checkout() {
    const { items, subtotal, clear } = useCart();
    const { user } = useAuth();
    const nav = useNavigate();
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState({
        shipping_name: user?.name || "",
        shipping_email: user?.email || "",
        shipping_phone: user?.phone || "",
        shipping_address: "",
        shipping_city: "Kampala",
        shipping_country: "Uganda",
        payment_method: "stripe",
        notes: "",
    });

    useEffect(() => {
        if (user) {
            setForm((f) => ({
                ...f,
                shipping_name: f.shipping_name || user.name || "",
                shipping_email: f.shipping_email || user.email || "",
                shipping_phone: f.shipping_phone || user.phone || "",
            }));
        }
    }, [user]);

    useEffect(() => {
        if (!items.length) nav("/cart");
    }, [items.length, nav]);

    const shipping = subtotal >= 100 ? 0 : 5;
    const total = subtotal + shipping;

    function set(k, v) {
        setForm((f) => ({ ...f, [k]: v }));
    }

    async function submit(e) {
        e.preventDefault();
        if (!items.length) return;
        setLoading(true);
        try {
            const payload = {
                items: items.map((i) => ({
                    product_id: i.product_id,
                    name: i.name,
                    price: i.price,
                    quantity: i.quantity,
                    image_url: i.image_url || "",
                })),
                ...form,
                origin_url: window.location.origin,
            };
            const { data } = await api.post("/orders", payload);
            if (data.checkout_url) {
                clear();
                window.location.href = data.checkout_url;
            } else {
                clear();
                toast.success("Order placed successfully!");
                nav(`/payment/success?order_number=${data.order.order_number}`);
            }
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not place order");
        } finally {
            setLoading(false);
        }
    }

    if (!items.length) return null;

    return (
        <div className="max-w-[1200px] mx-auto px-4 md:px-8 py-12" data-testid="checkout-page">
            <Link to="/cart" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mb-6">
                <ArrowLeft className="w-4 h-4" /> Back to cart
            </Link>
            <h1 className="font-display text-4xl md:text-5xl font-bold mb-10">Checkout</h1>
            <form onSubmit={submit} className="grid md:grid-cols-[1fr_400px] gap-8" data-testid="checkout-form">
                <div className="space-y-8">
                    {/* Shipping */}
                    <section className="bg-white border border-border p-6">
                        <h2 className="font-display font-bold text-xl mb-4">Delivery details</h2>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <input required value={form.shipping_name} onChange={(e) => set("shipping_name", e.target.value)} placeholder="Full name" className="border border-border px-3 py-2.5 focus:outline-none focus:border-primary" data-testid="checkout-name" />
                            <input required type="email" value={form.shipping_email} onChange={(e) => set("shipping_email", e.target.value)} placeholder="Email address" className="border border-border px-3 py-2.5 focus:outline-none focus:border-primary" data-testid="checkout-email" />
                            <input required value={form.shipping_phone} onChange={(e) => set("shipping_phone", e.target.value)} placeholder="Phone number" className="border border-border px-3 py-2.5 focus:outline-none focus:border-primary" data-testid="checkout-phone" />
                            <input required value={form.shipping_city} onChange={(e) => set("shipping_city", e.target.value)} placeholder="City" className="border border-border px-3 py-2.5 focus:outline-none focus:border-primary" data-testid="checkout-city" />
                            <input required value={form.shipping_address} onChange={(e) => set("shipping_address", e.target.value)} placeholder="Street address / building / area" className="sm:col-span-2 border border-border px-3 py-2.5 focus:outline-none focus:border-primary" data-testid="checkout-address" />
                            <input value={form.shipping_country} onChange={(e) => set("shipping_country", e.target.value)} placeholder="Country" className="border border-border px-3 py-2.5 focus:outline-none focus:border-primary" data-testid="checkout-country" />
                            <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Order notes (optional)" rows={2} className="sm:col-span-2 border border-border px-3 py-2.5 focus:outline-none focus:border-primary" data-testid="checkout-notes" />
                        </div>
                    </section>

                    {/* Payment */}
                    <section className="bg-white border border-border p-6">
                        <h2 className="font-display font-bold text-xl mb-4">Payment method</h2>
                        <div className="space-y-3">
                            {PAYMENT_METHODS.map((m) => (
                                <label
                                    key={m.id}
                                    className={`flex items-start gap-3 p-4 border cursor-pointer transition-colors ${
                                        form.payment_method === m.id ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
                                    }`}
                                    data-testid={`payment-${m.id}`}
                                >
                                    <input
                                        type="radio"
                                        name="payment"
                                        value={m.id}
                                        checked={form.payment_method === m.id}
                                        onChange={() => set("payment_method", m.id)}
                                        className="mt-1.5 accent-primary"
                                    />
                                    <m.icon className="w-5 h-5 text-primary mt-0.5" />
                                    <div>
                                        <div className="font-semibold">{m.label}</div>
                                        <div className="text-xs text-muted-foreground">{m.desc}</div>
                                    </div>
                                </label>
                            ))}
                        </div>
                    </section>
                </div>

                {/* Summary */}
                <aside className="bg-surface p-6 h-fit border border-border md:sticky md:top-24" data-testid="checkout-summary">
                    <h2 className="font-display font-bold text-xl mb-4">Order summary</h2>
                    <div className="space-y-3 max-h-60 overflow-y-auto mb-4">
                        {items.map((i) => (
                            <div key={i.product_id} className="flex gap-3 text-sm">
                                <div className="w-12 h-12 bg-white border border-border overflow-hidden flex-shrink-0">
                                    {i.image_url && <img src={i.image_url} alt={i.name} className="w-full h-full object-cover" />}
                                </div>
                                <div className="flex-1">
                                    <div className="font-semibold leading-tight line-clamp-2">{i.name}</div>
                                    <div className="text-muted-foreground text-xs">× {i.quantity}</div>
                                </div>
                                <div className="font-semibold">{money(i.price * i.quantity)}</div>
                            </div>
                        ))}
                    </div>
                    <div className="space-y-2 text-sm border-t border-border pt-4 mb-4">
                        <div className="flex justify-between"><span>Subtotal</span><span>{money(subtotal)}</span></div>
                        <div className="flex justify-between"><span>Shipping</span><span>{money(shipping)}</span></div>
                    </div>
                    <div className="flex justify-between font-display font-bold text-2xl mb-6 border-t border-border pt-4">
                        <span>Total</span><span data-testid="checkout-total">{money(total)}</span>
                    </div>
                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-primary text-white px-6 py-3 font-semibold hover:bg-primary-hover transition-colors disabled:opacity-50"
                        data-testid="checkout-submit-btn"
                    >
                        {loading ? "Processing…" : `Place order · ${money(total)}`}
                    </button>
                </aside>
            </form>
        </div>
    );
}
